// src/lib/zillowUrl.js
// Build a Zillow search URL for any address string. Zillow's /homes/{query}_rb/
// endpoint resolves any well-formed address query to the property detail page,
// so we don't need a Zillow API key or a stored zpid to link out.

export function zillowUrl(address) {
  const raw = String(address || '').trim();
  if (!raw) return null;
  // Zillow's _rb slug: hyphenate whitespace, keep commas + digits, drop
  // duplicates. URL-encoding preserves any character we didn't collapse.
  const slug = raw
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/,\s*/g, ', ')
    .replace(/\s/g, '-');
  return `https://www.zillow.com/homes/${encodeURIComponent(slug)}_rb/`;
}

// Convenience: build from parts { line1, city, state, zip } → normalized string
// then Zillow URL. Returns null when there's not enough to link.
export function zillowUrlFromParts({ line1, city, state, zip } = {}) {
  if (!line1) return null;
  const stateZip = [state, zip].filter(Boolean).join(' ');
  const full = [line1, city, stateZip].filter(Boolean).join(', ');
  return zillowUrl(full);
}

// Extract a human-readable address from a Zillow /homedetails/ URL.
// Pattern: https://www.zillow.com/homedetails/{slug}/{zpid}_zpid/
//   slug is dashed: "4834-Bexley-Dr-Stone-Mountain-GA-30083"
// Returns something like: "4834 Bexley Dr, Stone Mountain, GA 30083"
// Returns null if the URL isn't a recognized Zillow property page.
export function addressFromZillowUrl(url) {
  const m = String(url || '').match(/zillow\.com\/homedetails\/([^/]+)\/(\d+)_zpid/i);
  if (!m) return null;
  const slug = m[1];
  const parts = slug.split('-').filter(Boolean);
  if (parts.length < 4) return null;
  // Last token: zip (5 digits or 5+4). Preceded by state (2 letters).
  const zip = parts[parts.length - 1];
  const state = parts[parts.length - 2];
  if (!/^\d{5}(-\d{4})?$/.test(zip) || !/^[A-Za-z]{2}$/.test(state)) {
    return slug.replace(/-/g, ' ');
  }
  // Everything before state is street + city. Zillow doesn't mark the boundary,
  // so we join with spaces and let the reader see it. Add commas around city
  // when we can guess — assume the street ends at a common suffix (Dr, St, etc.)
  // or the 2nd-to-4th token, whichever comes first.
  const middle = parts.slice(0, parts.length - 2);
  const SUFFIXES = new Set(['dr','drive','st','street','ave','avenue','rd','road','ln','lane','blvd','boulevard','ct','court','way','pl','place','cir','circle','ter','terrace','pkwy','parkway','hwy','highway','trl','trail','xing','crossing','overlook','pt','point','sq','square','run','row']);
  let splitIdx = -1;
  for (let i = 1; i < middle.length; i++) {
    if (SUFFIXES.has(middle[i].toLowerCase())) { splitIdx = i + 1; break; }
  }
  if (splitIdx < 0 || splitIdx >= middle.length) {
    return `${middle.join(' ')}, ${state.toUpperCase()} ${zip}`;
  }
  const street = middle.slice(0, splitIdx).join(' ');
  const city = middle.slice(splitIdx).join(' ');
  return `${street}, ${city}, ${state.toUpperCase()} ${zip}`;
}

// Pull every Zillow /homedetails/ URL out of a blob of text. Case-insensitive,
// tolerates trailing punctuation.
export function extractZillowUrls(text) {
  const re = /https?:\/\/(?:www\.)?zillow\.com\/homedetails\/[^\s)"'<>]+/gi;
  const out = String(text || '').match(re) || [];
  return Array.from(new Set(out.map(u => u.replace(/[.,;]+$/, ''))));
}
